/**
 * Round, green daily-reward button. Mounts directly to `document.body`
 * and sits beneath the existing account button (top:64px right:16px).
 *
 * Three states: READY, COOLDOWN, HIDDEN. The owner calls `refresh()` on
 * sign-in/out and on screen transitions. A 60-second internal timer keeps
 * the cooldown hour count fresh while the user is on the menu.
 */

import type { StorageManager, ActiveReward } from '@/storage/save-data';
import { canClaim, isActive, hoursUntilClaim, rewardById } from '@/game/daily-rewards';

export class DailyRewardButton {
  private wrapper: HTMLDivElement | null = null;
  private button: HTMLButtonElement | null = null;
  private tickHandle: number | null = null;

  onClick: (() => void) | null = null;

  constructor(
    private storage: StorageManager,
    private nowFn: () => number = () => Date.now(),
  ) {}

  mount(): void {
    if (this.wrapper) return;
    const w = document.createElement('div');
    w.id = 'daily-reward-btn-wrapper';
    w.style.cssText = 'position:fixed;top:64px;right:16px;z-index:65;';

    const b = document.createElement('button');
    b.id = 'daily-reward-btn';
    b.style.cssText = [
      'width:56px;height:56px;border-radius:50%;',
      'background:#00ff66;color:#0a0a1a;border:2px solid #00ff66;',
      "font-family:'Courier New',monospace;font-weight:bold;font-size:0.55rem;",
      'line-height:1.05;text-align:center;cursor:pointer;',
      'box-shadow:0 0 14px rgba(0,255,102,0.55);',
      'backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);',
      'transition:opacity 0.2s;',
    ].join('');
    b.textContent = 'DAILY REWARD';
    b.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (this.button?.dataset.state === 'ready') this.onClick?.();
    });
    w.appendChild(b);
    document.body.appendChild(w);
    this.wrapper = w;
    this.button = b;

    this.tickHandle = window.setInterval(() => this.refresh(), 60_000);
    this.refresh();
  }

  setVisible(visible: boolean): void {
    if (!this.wrapper) return;
    if (!visible) {
      this.wrapper.style.display = 'none';
    } else {
      this.wrapper.style.display = '';
      this.refresh();
    }
  }

  /** Re-evaluate session + claim state and update the button DOM. */
  refresh(): void {
    if (!this.wrapper || !this.button) return;
    const session = this.storage.getSession();
    // DP - for testing
    //if (!session) {
    //  this.wrapper.style.display = 'none';
    //  return;
    //}

    const now = this.nowFn();
    const state = this.storage.getDailyReward();
    const claimedAt = state?.claimedAt;
    const ready = canClaim(claimedAt, now);
    const active = isActive(claimedAt, now);

    // DP - for testing
    //if ( ready) {
      this.button.dataset.state = 'ready';
      this.button.style.background = '#00ff66';
      this.button.style.color = '#0a0a1a';
      this.button.style.borderColor = '#00ff66';
      this.button.style.boxShadow = '0 0 14px rgba(0,255,102,0.55)';
      this.button.style.cursor = 'pointer';
      this.button.style.opacity = '1';
      this.button.textContent = 'DAILY REWARD';
    // } else if (active) {
    //   this.button.dataset.state = 'cooldown';
    //   this.button.style.background = '#0a1a10';
    //   this.button.style.color = '#88aa99';
    //   this.button.style.borderColor = '#225533';
    //   this.button.style.boxShadow = 'none';
    //   this.button.style.cursor = 'default';
    //   this.button.style.opacity = '0.85';
    //   const hrs = hoursUntilClaim(claimedAt, now);
    //   const icon = rewardIcon(state?.reward);
    //   this.button.textContent = `${icon} ${hrs}h`;
    // } else {
    //   // No active reward and not ready means nothing has been claimed for
    //   // 24h+. This is the same as "ready" from the player's POV.
    //   this.button.dataset.state = 'ready';
    //   this.button.textContent = 'DAILY REWARD';
    // }
  }

  destroy(): void {
    if (this.tickHandle !== null) {
      window.clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
    this.wrapper?.remove();
    this.wrapper = null;
    this.button = null;
  }
}

function rewardIcon(r: ActiveReward | undefined): string {
  if (!r) return '★';
  const def = rewardById(r.rewardId);
  if (!def) return '★';
  switch (def.kind) {
    case 'color':         return '●';
    case 'shape':         return '▲';
    case 'googly-eyes':   return '👀';
    case 'rainbow-trail': return '🌈';
  }
}
